import { memo } from 'react'

import moment from 'moment'

import { Assignment, Collections } from '@mui/icons-material'

import {
  Card,
  CardImage,
  CardFooter,
  ConfirmationDialog,
  CardFooterText,
  Chip,
  Stack,
  Button,
  useDialogState,
} from '@collaborate-health/uikit'
import { useDispatch, useSelector } from '@tabeeb/store'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { sessionTitleFormatter, useFormTitleFormatter } from '@tabeeb/modules/shared/utils/text'

import { attachForm, detachForm } from '../../actions'
import { getIsDetachButtonDisabled } from '../../selectors'
import { Form } from '../../types'

export interface AvailableFormsItemProps {
  attached?: boolean
  disabled?: boolean
  form: Form
}

const formPreviewPlaceholder = new URL('@tabeeb/assets/images/image_placeholder.png', import.meta.url).href

const AvailableFormsItem: React.FC<AvailableFormsItemProps> = ({ attached = false, disabled = false, form }) => {
  const dispatch = useDispatch()

  const contentId = useSelector(getContentId)
  const isDetachButtonDisabled = useSelector((state) => getIsDetachButtonDisabled(state, { form }))

  const { f } = useFormTitleFormatter()

  const [open, onOpen, onClose] = useDialogState()

  const attachFormToContent = () => {
    dispatch(
      attachForm.request({
        ContentId: contentId,
        FormId: form.FormId,
        IsAttachedFromCondition: false,
      })
    )
  }

  return (
    <>
      <Card>
        <CardImage backgroundPosition='top' image={form.IconUrl || formPreviewPlaceholder} title={form.Name!}>
          <Stack alignItems='flex-start' alignSelf='end' spacing={0.5}>
            <Chip
              color='primary'
              icon={<Collections />}
              label={sessionTitleFormatter.format(`Sessions: ${form.SessionsNumber}`)}
              size='small'
            />
            <Chip color='primary' icon={<Assignment />} label={`Controls: ${form.ControlsNumber}`} size='small' />
          </Stack>
        </CardImage>
        <CardFooter>
          <CardFooterText title={form.Name!} subtitle={moment(form.CreatedOn).format('D MMMM YYYY')} />
          <Button
            disabled={disabled || isDetachButtonDisabled}
            size='small'
            color='primary'
            variant={attached ? 'contained' : 'outlined'}
            onClick={attached ? onOpen : attachFormToContent}
          >
            {attached ? 'Detach' : 'Attach'}
          </Button>
        </CardFooter>
      </Card>

      <ConfirmationDialog
        isOpen={open}
        title={f('Are you sure you want to detach this form?')}
        dialogMessage={f('This form will be detached. You can attach it again later if needed')}
        onCloseDialog={onClose}
        submitButtonText='Detach'
        submitButtonColor='error'
        onSubmit={() => {
          dispatch(detachForm.request({ contentId, formId: form.FormId }))
          onClose()
        }}
      />
    </>
  )
}

export default memo(AvailableFormsItem)

